import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assistantIntents, type AssistantIntent } from './assistantIntents';
import { getFavorites, getUsage, recordIntentUse, toggleFavorite } from './intentUsage';
import { api } from './api';

type Platform = 'mobile' | 'desktop';

// Cuántas sugerencias ve realmente el usuario (coincide con el limit de
// SuggestedActions). La impresión que registramos refleja lo visible.
const SHOWN_LIMIT = 5;

export type SuggestionContext = {
  /** Hay consultas sin leer → priorizar revisar consultas. */
  hasUnreadLeads?: boolean;
  /** Aún no se publica el sitio → priorizar publicar. */
  needsPublish?: boolean;
};

/**
 * Ordena las acciones sugeridas combinando: favoritos del usuario, frecuencia
 * de uso, señales de contexto del negocio y, al final, el orden del catálogo.
 * No inventa intents: solo reordena `assistantIntents` filtrados por plataforma.
 */
export function useAssistantSuggestions(platform: Platform, context: SuggestionContext = {}) {
  const [favorites, setFavorites] = useState<string[]>(() => getFavorites());
  const [usage, setUsage] = useState<Record<string, number>>(() => getUsage());

  const contextBoost = useCallback(
    (intent: AssistantIntent): number => {
      if (context.hasUnreadLeads && intent.id === 'check-new-leads') return 100;
      if (context.needsPublish && intent.id === 'publish-site') return 80;
      return 0;
    },
    [context.hasUnreadLeads, context.needsPublish],
  );

  const suggestions = useMemo(() => {
    return assistantIntents
      .filter((intent) => intent.platforms.includes(platform))
      .map((intent) => {
        const isFavorite = favorites.includes(intent.id);
        const score =
          (isFavorite ? 1000 : 0) + contextBoost(intent) + (usage[intent.id] ?? 0);
        return { intent, isFavorite, score };
      })
      .sort((a, b) => b.score - a.score);
  }, [platform, favorites, usage, contextBoost]);

  // Telemetría de impresiones ('shown'): una sola vez por cada set visible de
  // sugerencias, no por pulsación. El denominador para medir abandono (mostrado
  // vs usado) en la Fase 5. Best-effort: nunca rompe el render.
  const lastShown = useRef<string>('');
  useEffect(() => {
    const visibleIds = suggestions.slice(0, SHOWN_LIMIT).map((s) => s.intent.id);
    const signature = visibleIds.join(',');
    if (signature === '' || signature === lastShown.current) return;
    lastShown.current = signature;
    api.assistant
      .recordEvent({ eventType: 'shown', meta: { surface: 'suggested-actions', intentIds: visibleIds } })
      .catch(() => { /* ignore */ });
  }, [suggestions]);

  const recordUse = useCallback((id: string) => {
    setUsage(recordIntentUse(id));
  }, []);

  const onToggleFavorite = useCallback((id: string) => {
    setFavorites(toggleFavorite(id));
  }, []);

  return { suggestions, recordUse, toggleFavorite: onToggleFavorite };
}
